/**
 * The trust score behind the ring on a listing tile.
 *
 * The site has always shown seller feedback and review count as two raw
 * numbers side by side, which asks the visitor to do the comparison. The
 * Console tile shows one number instead, and the ring is only worth having if
 * that number means the same thing everywhere it appears -- so the weights
 * live here and nowhere else, and products/trust_score.py is checked against
 * these constants by test_trust_score.py.
 *
 * Three deliberate choices, none of them obvious:
 *
 * 1. **The feedback ramp starts at 98%, not 0%.** Every seller the catalog
 *    carries sits between 97 and 100, so a 0-100 scale would put the whole
 *    catalog in the top 3% of the ring and the fill would look identical on
 *    every card. 98 to 100 is where the actual spread is, and it is what makes
 *    99.2% and 99.9% look different.
 *
 * 2. **Review count is logarithmic.** 50 sales to 500 is a real change in what
 *    the feedback percentage is evidence *of*; 14,000 to 21,000 is not. The
 *    curve tops out at 10,000.
 *
 * 3. **What we do not know is never scored as zero.** A seller whose return
 *    policy eBay did not report is scored out of 85 and rescaled, not docked
 *    15 points -- the same call floor-state.js makes when it lets an unknown
 *    return policy clear a floor. If feedback or review count is missing there
 *    is no score at all: score() returns null and the tile says the seller is
 *    unrated rather than drawing a ring around a guess. That is not an edge
 *    case today -- see the coverage note in products/trust_floor.py.
 *
 * The weights themselves are still a proposal (see Data gaps in the plan).
 * They are constants here so that changing them is one edit and one test
 * update, not a hunt through the renderers.
 */

import { FLOOR_NAMES, sellerNumbers, tier } from './floor-state.js';

/** Points available for each input. They need not sum to 100; the score is a ratio. */
export const WEIGHTS = { feedback: 60, reviews: 25, returns: 15 };

/** Below this feedback percentage the feedback component scores nothing. */
export const FEEDBACK_FLOOR = 98.0;

/** At or above this it scores full marks. */
export const FEEDBACK_CEILING = 100.0;

/** Review count at which the review component tops out. */
export const REVIEWS_CAP = 10000;

/** Tier index -> the floor that tier clears. Index 0 clears none. */
export const TIER_FLOORS = ['', 'standard', 'strict', 'elite'];

/**
 * 0-100, or null when the seller carries no rating at all.
 *
 * Field aliases are floor-state's job, not this module's: the score and the
 * floor have to read the same numbers off the same row, and two alias tables
 * is how they would stop doing that.
 *
 * @param {object} seller
 * @returns {number|null}
 */
export function score(seller) {
  const { feedback, reviews, returns } = sellerNumbers(seller);
  if (!Number.isFinite(feedback) || !Number.isFinite(reviews)) return null;

  const span = FEEDBACK_CEILING - FEEDBACK_FLOOR;
  const feedbackRatio = Math.min(1, Math.max(0, (feedback - FEEDBACK_FLOOR) / span));
  const reviewsRatio = Math.min(1, Math.log10(Math.max(reviews, 1)) / Math.log10(REVIEWS_CAP));

  let earned = feedbackRatio * WEIGHTS.feedback + reviewsRatio * WEIGHTS.reviews;
  let possible = WEIGHTS.feedback + WEIGHTS.reviews;

  // null means eBay did not report a return policy. Scoring that as a refusal
  // would punish the seller for our missing data.
  if (returns !== null && returns !== undefined) {
    possible += WEIGHTS.returns;
    if (returns) earned += WEIGHTS.returns;
  }

  return Math.round((earned / possible) * 100);
}

/**
 * Everything a ring needs: the number, the tier, and the sentence a screen
 * reader gets. Null when the seller is unrated, so callers branch once.
 *
 * @returns {{score: number, tier: number, label: string}|null}
 */
export function trust(seller) {
  const value = score(seller);
  if (value === null) return null;
  const level = tier(seller);
  const floor = TIER_FLOORS[level];
  return {
    score: value,
    tier: level,
    label: floor
      ? `Trust score ${value}, clears ${FLOOR_NAMES[floor]}`
      : `Trust score ${value}, clears no trust floor`,
  };
}
